import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  TrendingUp, BarChart3, Table, Type, Minus, Gauge, ArrowLeftRight, Image,
  ChartPie as PieChartIcon, Save, Download, Plus, Trash2, Copy, Search,
  GripVertical, Settings, ChevronLeft, Check, X, Eye, PanelRight
, Clock, Link2} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import reportBuilderService from '../services/reportBuilderService';
import KPICardWidget from '../components/ReportBuilder/widgets/KPICardWidget';
import LineChartWidget from '../components/ReportBuilder/widgets/LineChartWidget';
import BarChartWidget from '../components/ReportBuilder/widgets/BarChartWidget';
import PieChartWidget from '../components/ReportBuilder/widgets/PieChartWidget';
import DataTableWidget from '../components/ReportBuilder/widgets/DataTableWidget';
import TextBlockWidget from '../components/ReportBuilder/widgets/TextBlockWidget';
import ScoreGaugeWidget from '../components/ReportBuilder/widgets/ScoreGaugeWidget';
import ComparisonWidget from '../components/ReportBuilder/widgets/ComparisonWidget';
import DividerWidget from '../components/ReportBuilder/widgets/DividerWidget';
import ImageBlockWidget from '../components/ReportBuilder/widgets/ImageBlockWidget';
import PropertiesPanel from '../components/ReportBuilder/PropertiesPanel';
import ScheduleReportModal from '../components/ReportBuilder/ScheduleReportModal';
import ShareReportModal from '../components/ReportBuilder/ShareReportModal';



const WIDGET_MAP = {
  kpi_card: KPICardWidget,
  line_chart: LineChartWidget,
  bar_chart: BarChartWidget,
  pie_chart: PieChartWidget,
  data_table: DataTableWidget,
  text_block: TextBlockWidget,
  score_gauge: ScoreGaugeWidget,
  comparison_card: ComparisonWidget,
  divider: DividerWidget,
  image_block: ImageBlockWidget
};

const WIDGET_CATALOG = [
  { id: 'kpi_card', name: 'KPI Card', icon: TrendingUp, category: 'metrics', description: 'Single metric with trend', defaultSize: { w: 3, h: 2, minW: 2, minH: 2 } },
  { id: 'line_chart', name: 'Line Chart', icon: TrendingUp, category: 'charts', description: 'Time-series trends', defaultSize: { w: 6, h: 4, minW: 4, minH: 3 } },
  { id: 'bar_chart', name: 'Bar Chart', icon: BarChart3, category: 'charts', description: 'Comparison chart', defaultSize: { w: 6, h: 4, minW: 4, minH: 3 } },
  { id: 'pie_chart', name: 'Pie Chart', icon: PieChartIcon, category: 'charts', description: 'Distribution chart', defaultSize: { w: 4, h: 4, minW: 3, minH: 3 } },
  { id: 'data_table', name: 'Data Table', icon: Table, category: 'data', description: 'Sortable data table', defaultSize: { w: 12, h: 5, minW: 6, minH: 3 } },
  { id: 'text_block', name: 'Text Block', icon: Type, category: 'layout', description: 'Custom text or heading', defaultSize: { w: 12, h: 2, minW: 3, minH: 1 } },
  { id: 'divider', name: 'Divider', icon: Minus, category: 'layout', description: 'Section separator', defaultSize: { w: 12, h: 1, minW: 4, minH: 1 } },
  { id: 'score_gauge', name: 'Score Gauge', icon: Gauge, category: 'metrics', description: 'Circular score', locked: false, defaultSize: { w: 3, h: 3, minW: 2, minH: 2 } },
  { id: 'comparison_card', name: 'Comparison', icon: ArrowLeftRight, category: 'metrics', description: 'Period comparison', locked: false, defaultSize: { w: 4, h: 3, minW: 3, minH: 2 } },
  { id: 'image_block', name: 'Image/Logo', icon: Image, category: 'layout', description: 'Custom image', locked: false, defaultSize: { w: 4, h: 3, minW: 2, minH: 2 } }
];

const CATEGORIES = [
  { id: 'all', name: 'All Widgets' },
  { id: 'metrics', name: 'Metrics' },
  { id: 'charts', name: 'Charts' },
  { id: 'data', name: 'Data' },
  { id: 'layout', name: 'Layout' }
];

const ReportBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('id');

  const [widgets, setWidgets] = useState([]);
  const [layout, setLayout] = useState([]);
  const [reportName, setReportName] = useState('Untitled Report');
  const [isEditingName, setIsEditingName] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogFilter, setCatalogFilter] = useState('all');
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const exportRef = useRef(null);
  const canvasRef = useRef(null);
  const nameInputRef = useRef(null);
  const isLoadingRef = useRef(false);
  const [canvasWidth, setCanvasWidth] = useState(1200);

  const generateWidgetId = () => `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Track canvas container width
  React.useEffect(() => {
    if (!canvasRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanvasWidth(entry.contentRect.width);
      }
    });
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, []);

  // Load template if editing
  useEffect(() => {
    if (editId) {
      const loadTemplate = async () => {
        isLoadingRef.current = true;
        try {
          const data = await reportBuilderService.getTemplate(editId);
          const t = data.template || data;
          setReportName(t.name || 'Untitled Report');
          const w = typeof t.widgets === 'string' ? JSON.parse(t.widgets) : t.widgets;
          setWidgets(w || []);
          const l = typeof t.layout === 'string' ? JSON.parse(t.layout) : t.layout;
          if (l) setLayout(Array.isArray(l) ? l : (l.lg || []));
          setTemplateId(parseInt(editId));
          setHasChanges(false);
        } catch (err) {
          setTimeout(() => { isLoadingRef.current = false; }, 500);
          console.error('Failed to load template:', err);
        }
      };
      loadTemplate();
    }
  }, [editId]);

  // Focus name input when editing
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Add widget
  const addWidget = useCallback((catalogItem) => {
    const id = generateWidgetId();
    const newWidget = { id, type: catalogItem.id, config: { title: catalogItem.name } };
    const newLayoutItem = { i: id, x: 0, y: Infinity, ...catalogItem.defaultSize };
    setWidgets(prev => [...prev, newWidget]);
    setLayout(prev => [...prev, newLayoutItem]);
    setHasChanges(true);
    setSelectedWidgetId(id);
    setRightPanelOpen(true);
  }, []);

  // Remove widget
  const removeWidget = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    setLayout(prev => prev.filter(l => l.i !== widgetId));
    if (selectedWidgetId === widgetId) {
      setSelectedWidgetId(null);
      setRightPanelOpen(false);
    }
    setHasChanges(true);
  }, [selectedWidgetId]);

  // Duplicate widget
  const duplicateWidget = useCallback((widgetId) => {
    const sourceWidget = widgets.find(w => w.id === widgetId);
    const sourceLayout = layout.find(l => l.i === widgetId);
    if (!sourceWidget || !sourceLayout) return;
    const newId = generateWidgetId();
    setWidgets(prev => [...prev, { ...sourceWidget, id: newId }]);
    setLayout(prev => [...prev, { ...sourceLayout, i: newId, y: Infinity }]);
    setHasChanges(true);
  }, [widgets, layout]);

  // Update widget config
  const updateWidgetConfig = useCallback((widgetId, configUpdates) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, config: { ...w.config, ...configUpdates } } : w
    ));
    setHasChanges(true);
  }, []);

  // Layout change handler
  const onLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    if (!isLoadingRef.current) {
      setHasChanges(true);
    } else {
      setTimeout(() => { isLoadingRef.current = false; }, 500);
    }
  }, []);

  // Save template
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        name: reportName,
        widgets: JSON.stringify(widgets),
        layout: JSON.stringify({ lg: layout }),
        category: 'custom'
      };
      if (templateId) {
        await reportBuilderService.updateTemplate(templateId, payload);
      } else {
        const res = await reportBuilderService.saveTemplate(payload);
        const newId = res.template?.id || res.id;
        if (newId) {
          setTemplateId(newId);
          navigate(`/report-builder?id=${newId}`, { replace: true });
        }
      }
      setHasChanges(false);
      showToast('success', 'Report saved successfully!');
    } catch (err) {
      showToast('error', 'Failed to save report');
      console.error(err);
    }
    setIsSaving(false);
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  // A report may only leave the product if every data widget is bound to a real
  // source. Sample data no longer exists, but this stops an empty or unbound
  // report being exported as though it were a finished client deliverable.
  const NON_DATA_WIDGETS = ['text', 'divider', 'image', 'heading', 'spacer'];
  const unboundWidgets = (widgets || []).filter((w) => {
    const type = String(w.type || '').toLowerCase();
    if (NON_DATA_WIDGETS.some((t) => type.includes(t))) return false;
    const c = w.config || {};
    const hasRows = Array.isArray(c.data) && c.data.length > 0;
    return !c.dataSource && !hasRows;
  });
  const canExportPDF = (widgets || []).length > 0 && unboundWidgets.length === 0;

  // Export PDF
  const handleExportPDF = async () => {
    if (!exportRef.current || isExporting) return;
    if (!canExportPDF) {
      showToast('error', 'Connect a data source to every widget before exporting.');
      return;
    }
    setIsExporting(true);
    try {
      const el = exportRef.current;
      const origBg = el.style.backgroundColor;
      el.style.backgroundColor = '#ffffff';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 1200,
        removeContainer: true,
        onclone: (doc) => {
          const root = doc.getElementById('report-export-area');
          if (root) {
            root.style.backgroundColor = '#ffffff';
            root.querySelectorAll('*').forEach(node => {
              const bg = window.getComputedStyle(node).backgroundColor;
              if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
                node.style.backgroundColor = '#ffffff';
              }
            });
          }
        }
      });

      el.style.backgroundColor = origBg;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const margin = 10;
      const availableWidth = pdfWidth - (margin * 2);
      const ratio = availableWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;

      pdf.setFontSize(14);
      pdf.setTextColor(30, 41, 59);
      pdf.text(reportName, pdfWidth / 2, margin, { align: 'center' });

      const imgStartY = margin + 4;

      if (scaledHeight + imgStartY > pdfHeight - margin) {
        const pageContentHeight = pdfHeight - imgStartY - margin - 6;
        const sliceHeight = Math.floor(pageContentHeight / ratio);
        let srcY = 0;
        let page = 0;

        while (srcY < imgHeight) {
          if (page > 0) {
            pdf.addPage();
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
          }

          const currentSliceH = Math.min(sliceHeight, imgHeight - srcY);
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidth;
          tempCanvas.height = currentSliceH;
          const ctx = tempCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
          ctx.drawImage(canvas, 0, srcY, imgWidth, currentSliceH, 0, 0, imgWidth, currentSliceH);

          const sliceData = tempCanvas.toDataURL('image/png');
          const drawY = page === 0 ? imgStartY : margin;
          pdf.addImage(sliceData, 'PNG', margin, drawY, availableWidth, currentSliceH * ratio);

          pdf.setFontSize(7);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Generated by SERPfinity Report Builder', pdfWidth / 2, pdfHeight - 4, { align: 'center' });

          srcY += currentSliceH;
          page++;
        }
      } else {
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', margin, imgStartY, availableWidth, scaledHeight);
        pdf.setFontSize(7);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Generated by SERPfinity Report Builder', pdfWidth / 2, pdfHeight - 4, { align: 'center' });
      }

      const fileName = reportName.replace(/[^a-zA-Z0-9]/g, '_') + '.pdf';
      pdf.save(fileName);
      showToast('success', 'PDF exported successfully!');
    } catch (err) {
      console.error('PDF export error:', err);
      showToast('error', 'Failed to export PDF');
    }
    setIsExporting(false);
  };

  // Selected widget for properties panel
  const selectedWidget = widgets.find(w => w.id === selectedWidgetId);

  // Filtered catalog
  const filteredCatalog = WIDGET_CATALOG.filter(w => {
    if (catalogFilter !== 'all' && w.category !== catalogFilter) return false;
    if (catalogSearch && !w.name.toLowerCase().includes(catalogSearch.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-100">
      <style dangerouslySetInnerHTML={{ __html: `
        .react-grid-item > .react-resizable-handle {
          opacity: 1 !important;
          z-index: 20 !important;
        }
        .react-grid-item > .react-resizable-handle::after {
          width: 8px !important;
          height: 8px !important;
          border-right: 3px solid rgba(59, 130, 246, 0.6) !important;
          border-bottom: 3px solid rgba(59, 130, 246, 0.6) !important;
        }
        .react-grid-item > .react-resizable-handle:hover::after {
          border-color: rgba(59, 130, 246, 1) !important;
        }
        .react-grid-item.react-grid-placeholder {
          background: rgba(59, 130, 246, 0.15) !important;
          border: 2px dashed rgba(59, 130, 246, 0.4) !important;
          border-radius: 8px;
        }
      ` }} />
      {/* Top toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/reports')} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
            <ChevronLeft className="w-5 h-5" />
          </button>
          {isEditingName ? (
            <input ref={nameInputRef} type="text" value={reportName}
              onChange={e => setReportName(e.target.value)}
              onBlur={() => { setIsEditingName(false); setHasChanges(true); }}
              onKeyDown={e => { if (e.key === 'Enter') { setIsEditingName(false); setHasChanges(true); } if (e.key === 'Escape') setIsEditingName(false); }}
              className="text-lg font-semibold text-gray-800 border-b-2 border-blue-500 outline-none bg-transparent px-1" />
          ) : (
            <h1 onClick={() => setIsEditingName(true)} className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors">
              {reportName}
            </h1>
          )}
          {hasChanges && <span className="text-xs text-amber-500 font-medium bg-amber-50 px-2 py-0.5 rounded">Unsaved</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              previewMode ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <Eye className="w-4 h-4" />{previewMode ? 'Editing' : 'Preview'}
          </button>
          <button onClick={handleSave} disabled={isSaving || !hasChanges}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Save className="w-4 h-4" />{isSaving ? 'Saving...' : 'Save'}
          </button>
          {templateId && (
                <>
                  <button onClick={() => setShowScheduleModal(true)} className="flex items-center gap-1.5 px-4 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    <Clock className="w-4 h-4" /> Schedule
                  </button>
                  <button onClick={() => setShowShareModal(true)} className="flex items-center gap-1.5 px-4 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    <Link2 className="w-4 h-4" /> Share
                  </button>
                </>
              )}
              <button onClick={handleExportPDF} disabled={isExporting || !canExportPDF}
              title={canExportPDF ? 'Export this report as a PDF' : 'Connect a data source to every widget before exporting'}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50 transition-colors">
            <Download className="w-4 h-4" />{isExporting ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Widget catalog */}
        {!previewMode && (
          <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search widgets..." value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
            </div>

            <div className="flex gap-1 px-3 py-2 border-b border-gray-100 overflow-x-auto">
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setCatalogFilter(cat.id)}
                  className={`px-2 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                    catalogFilter === cat.id ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'
                  }`}>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredCatalog.map(item => {
                const Icon = item.icon;
                return (
                  <button key={item.id} onClick={() => addWidget(item)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all group text-left">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700">{item.name}</p>
                      <p className="text-xs text-gray-400 truncate">{item.description}</p>
                    </div>
                    <Plus className="w-4 h-4 text-gray-300 group-hover:text-blue-500 ml-auto flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Center - Report canvas */}
        <div className="flex-1 overflow-auto p-6" ref={canvasRef}>
          <div ref={exportRef} id="report-export-area"
            className="mx-auto bg-white rounded-xl shadow-sm border border-gray-200 min-h-[600px]"
            style={{ maxWidth: 1200 }}>
            {widgets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 text-gray-400">
                <Plus className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-lg font-medium mb-1">Start Building Your Report</p>
                <p className="text-sm">Click a widget from the left panel to add it</p>
              </div>
            ) : (
              <ResponsiveGridLayout
                className="layout"
                layouts={{ lg: layout }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={80}
                width={canvasWidth || 1200}
                onLayoutChange={(newLayout) => onLayoutChange(newLayout)}
                isDraggable={!previewMode}
                isResizable={!previewMode}
                resizeHandles={["se"]}
                draggableHandle=".drag-handle"
                compactType="vertical"
                margin={[12, 12]}
              >
                {widgets.map(widget => {
                  const WidgetComp = WIDGET_MAP[widget.type];
                  const isSelected = selectedWidgetId === widget.id && !previewMode;
                  return (
                    <div key={widget.id}
                      className={`group relative rounded-lg border-2 transition-all ${
                        isSelected ? 'border-blue-500 shadow-lg shadow-blue-100' : 'border-transparent hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (!previewMode) {
                          setSelectedWidgetId(widget.id);
                          setRightPanelOpen(true);
                        }
                      }}>
                      {/* Widget toolbar */}
                      {!previewMode && (
                        <div className={`absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-2 py-1 bg-gradient-to-b from-black/40 to-transparent transition-opacity ${
                          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}>
                          <div className="drag-handle cursor-grab active:cursor-grabbing p-0.5">
                            <GripVertical className="w-4 h-4 text-white/80" />
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={(e) => { e.stopPropagation(); duplicateWidget(widget.id); }}
                              className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white">
                              <Copy className="w-3 h-3" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); removeWidget(widget.id); }}
                              className="p-1 rounded hover:bg-red-500/50 text-white/80 hover:text-white">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Widget content */}
                      <div className="h-full bg-white">
                        {WidgetComp ? <WidgetComp config={widget.config} /> : (
                          <div className="h-full flex items-center justify-center text-gray-400 text-sm">{widget.type}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </ResponsiveGridLayout>
            )}
          </div>
        </div>

        {/* Right panel - Properties */}
        {!previewMode && rightPanelOpen && selectedWidget && (
          <div className="w-72 border-l border-gray-200 flex-shrink-0 overflow-hidden">
            <PropertiesPanel
              widget={selectedWidget}
              onUpdateConfig={updateWidgetConfig}
              onClose={() => { setRightPanelOpen(false); setSelectedWidgetId(null); }}
            />
          </div>
        )}

        {/* Right panel toggle when closed */}
        {!previewMode && (!rightPanelOpen || !selectedWidget) && (
          <div className="w-12 border-l border-gray-200 bg-white flex flex-col items-center pt-4 flex-shrink-0">
            <div className="flex flex-col items-center text-gray-400">
              <PanelRight className="w-5 h-5 mb-2" />
              <span className="text-xs writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>Properties</span>
            </div>
          </div>
        )}
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.type === 'success' ? <Check size={16} /> : <X size={16} />}
          {toast.message}
        </div>
      )}
    
      <ScheduleReportModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        templateId={templateId}
        templateName={reportName}
      />
      <ShareReportModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        templateId={templateId}
        templateName={reportName}
      />
    </div>
  );
};

export default ReportBuilder;
